import React from 'react';
import { ArrowLeft, Shield, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const Datenschutz = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-[#40E0D0]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zur Startseite
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E0F7FA] rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#40E0D0]" />
            </div>
            <h1 className="font-['Merriweather'] text-3xl font-bold text-slate-900">
              Datenschutzerklärung
            </h1>
          </div>

          <div className="prose prose-slate max-w-none space-y-8">
            {/* Verantwortlicher */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                1. Verantwortlicher
              </h2>
              <p className="text-slate-600">
                Verantwortlich für die Datenverarbeitung auf dieser Website ist:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <p className="text-slate-700">
                  <strong>Ambulante Pflege Großstadtherzen</strong><br />
                  Zschochersche Str. 49<br />
                  04229 Leipzig<br /><br />
                  <Phone className="w-4 h-4 inline mr-2" />
                  Telefon: 0341-24927833<br />
                  <Mail className="w-4 h-4 inline mr-2" />
                  E-Mail: info@grossstadtherzen.de
                </p>
              </div>
            </section>

            {/* Datenerfassung */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                2. Datenerfassung bei Terminbuchung
              </h2>
              <p className="text-slate-600 mb-4">
                Bei der Buchung eines Beratungstermins erheben wir folgende personenbezogene Daten:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Vor- und Nachname</li>
                <li>Adresse (Straße, PLZ, Stadt)</li>
                <li>Geburtsdatum</li>
                <li>Pflegegrad</li>
                <li>Name der Krankenkasse</li>
                <li>Versicherungsnummer</li>
                <li>Telefonnummer</li>
                <li>E-Mail-Adresse</li>
                <li>Gewählter Termin (Datum und Uhrzeit)</li>
              </ul>
              <p className="text-slate-600 mt-4">
                <strong>Rechtsgrundlage:</strong> Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO 
                zur Erfüllung des Beratungsvertrags sowie Art. 6 Abs. 1 lit. c DSGVO zur Erfüllung 
                unserer gesetzlichen Pflichten nach § 37 Abs. 3 SGB XI.
              </p>
            </section>

            {/* Datenverwendung */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                3. Verwendung Ihrer Daten
              </h2>
              <p className="text-slate-600 mb-4">
                Ihre Daten werden ausschließlich für folgende Zwecke verwendet:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Durchführung der Pflegeberatung nach § 37 Abs. 3 SGB XI</li>
                <li>Terminbestätigung per E-Mail</li>
                <li>Telefonischer Rückruf zum vereinbarten Termin</li>
                <li>Dokumentation gegenüber Ihrer Pflegekasse</li>
              </ul>
            </section>

            {/* E-Mail Versand */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                4. E-Mail-Versand (SendGrid)
              </h2>
              <p className="text-slate-600">
                Für den Versand von Terminbestätigungen nutzen wir den Dienst SendGrid 
                (Twilio Inc., 375 Beale Street, San Francisco, CA 94105, USA). 
                Dabei wird Ihre E-Mail-Adresse an SendGrid übermittelt. 
                SendGrid ist unter dem EU-US Data Privacy Framework zertifiziert.
              </p>
            </section>

            {/* Google Calendar */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                5. Kalenderintegration (Google Calendar)
              </h2>
              <p className="text-slate-600">
                Gebuchte Termine werden in unserem internen Google Calendar gespeichert, 
                um eine effiziente Terminverwaltung zu gewährleisten. Die Daten werden 
                ausschließlich intern verwendet und nicht an Dritte weitergegeben.
              </p>
            </section>

            {/* Cookies & Tracking */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                6. Cookies und Tracking
              </h2>
              
              <h3 className="font-semibold text-slate-900 mt-4 mb-2">Notwendige Cookies</h3>
              <p className="text-slate-600 mb-4">
                Diese Website verwendet technisch notwendige Cookies für die Sitzungsverwaltung 
                und die Speicherung Ihrer Cookie-Einstellungen.
              </p>
              
              <h3 className="font-semibold text-slate-900 mt-4 mb-2">Google Analytics & Google Ads</h3>
              <p className="text-slate-600">
                Mit Ihrer Einwilligung nutzen wir Google Analytics und Google Ads 
                (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland), 
                um die Nutzung unserer Website zu analysieren und unsere Werbung zu optimieren. 
                Dabei werden Cookies gesetzt und Daten an Google-Server übertragen. 
                Sie können Ihre Einwilligung jederzeit widerrufen, indem Sie Ihre Browser-Cookies löschen.
              </p>
            </section>

            {/* KI-Hinweis */}
            <section className="bg-[#E0F7FA] rounded-xl p-6">
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                7. Einsatz von Künstlicher Intelligenz (KI)
              </h2>
              <p className="text-slate-600 mb-4">
                Diese Website wurde unter Einsatz von Künstlicher Intelligenz (KI) erstellt und optimiert. 
                Im laufenden Betrieb werden folgende KI-gestützte Dienste eingesetzt:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 mb-4">
                <li>
                  <strong>Entwicklung & Wartung:</strong> Die Website wurde mit Unterstützung 
                  von KI-Technologie (Emergent/Claude) entwickelt.
                </li>
                <li>
                  <strong>Automatisierte Prozesse:</strong> Die Terminbuchung und 
                  E-Mail-Bestätigungen erfolgen automatisiert.
                </li>
              </ul>
              <p className="text-slate-600">
                <strong>Wichtig:</strong> Die eigentliche Pflegeberatung wird ausschließlich 
                von qualifizierten Pflegefachkräften durchgeführt – nicht von KI-Systemen. 
                Ihre persönlichen Gesundheitsdaten werden nicht für KI-Training verwendet.
              </p>
            </section>

            {/* Datensicherheit */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                8. Datensicherheit
              </h2>
              <p className="text-slate-600">
                Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, 
                um Ihre Daten gegen Manipulation, Verlust oder unberechtigten Zugriff zu schützen. 
                Die Datenübertragung erfolgt verschlüsselt über HTTPS/SSL.
              </p>
            </section>

            {/* Speicherdauer */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                9. Speicherdauer
              </h2>
              <p className="text-slate-600">
                Ihre Buchungsdaten werden für die Dauer der gesetzlichen Aufbewahrungsfristen 
                (in der Regel 10 Jahre) gespeichert und danach gelöscht, sofern keine 
                längeren Aufbewahrungspflichten bestehen.
              </p>
            </section>

            {/* Ihre Rechte */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                10. Ihre Rechte
              </h2>
              <p className="text-slate-600 mb-4">
                Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li><strong>Auskunftsrecht</strong> (Art. 15 DSGVO)</li>
                <li><strong>Berichtigungsrecht</strong> (Art. 16 DSGVO)</li>
                <li><strong>Löschungsrecht</strong> (Art. 17 DSGVO)</li>
                <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
                <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
                <li><strong>Widerspruchsrecht</strong> (Art. 21 DSGVO)</li>
              </ul>
              <p className="text-slate-600 mt-4">
                Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: 
                <a href="mailto:info@grossstadtherzen.de" className="text-[#40E0D0] hover:underline ml-1">
                  info@grossstadtherzen.de
                </a>
              </p>
            </section>

            {/* Beschwerderecht */}
            <section>
              <h2 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-4">
                11. Beschwerderecht
              </h2>
              <p className="text-slate-600">
                Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. 
                Die zuständige Aufsichtsbehörde ist:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <p className="text-slate-700">
                  Sächsischer Datenschutzbeauftragter<br />
                  Devrientstraße 5<br />
                  01067 Dresden<br />
                  saechsdsb@slt.sachsen.de
                </p>
              </div>
            </section>

            {/* Stand */}
            <section className="border-t border-slate-200 pt-6">
              <p className="text-slate-500 text-sm">
                Stand: Februar 2026
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Datenschutz;
